import React, { useState, useEffect } from 'react';
import { Input, Select, Radio, Tag } from 'antd';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';
import { DealStage, ProjectDto, ProjectDtoPaginatedList } from 'api/models';
import { useTranslation } from 'react-i18next';
import { ProjectsApi } from 'api/api';
import { DealStageConfig } from '../constants';

const projectsApi = new ProjectsApi();

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    
    .ant-input-search,
    .ant-select {
      flex: 1;
      min-width: 200px;
    }
  }
`;

const ViewToggle = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    height: 32px; // Match the height of inputs/selects

    .feather {
      margin: 0;
      vertical-align: middle;
    }
  }
`;

interface Props {
  viewMode: 'kanban' | 'table';
  onViewModeChange: (mode: 'kanban' | 'table') => void;
  onSearch: (value: string) => void;
  onStageFilter: (stage: DealStage | undefined) => void;
  onProjectFilter: (projectId: number | undefined) => void;
}

const DealsPageFilter: React.FC<Props> = ({ viewMode, onViewModeChange, onSearch, onStageFilter, onProjectFilter }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({
        pageSize: 100,
        pageNumber: 1,
        isActive: true,
      });
      setProjects(response.data.items || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <HeaderContainer>
      <FilterGroup>
        <Input.Search
          placeholder={t('deals:search-placeholder', 'Search deals...')}
          onSearch={onSearch}
          style={{ width: 200 }}
          size="middle"
        />

        <Select
          style={{ width: 200 }}
          placeholder={t('projects:select-filter', 'Filter by Project')}
          onChange={onProjectFilter}
          allowClear
          loading={projectsLoading}
        >
          {projects.map(project => (
            <Select.Option key={project.id} value={project.id}>
              {project.name}
            </Select.Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder={t('deals:stage-filter', 'Filter by Stage')}
          onChange={onStageFilter}
          allowClear
        >
          {Object.entries(DealStageConfig).map(([stage, config]) => (
            <Select.Option key={stage} value={stage}>
              <Tag color={config.color}>{t(config.key, config.title)}</Tag>
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>

      <ViewToggle value={viewMode} onChange={e => onViewModeChange(e.target.value)} optionType="button">
        <Radio.Button value="kanban">
          <FeatherIcon icon="layout" size={16} />
        </Radio.Button>
        <Radio.Button value="table">
          <FeatherIcon icon="list" size={16} />
        </Radio.Button>
      </ViewToggle>
    </HeaderContainer>
  );
};

export default DealsPageFilter;
